import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { getClientByToken, detachPaymentRequisite } from '../../core/_requests'
import { ROLE, useAuth } from '../../../auth'
import { IClientItem } from '../../core/_models'
import { CLIENTS_PAGE_BREADCRUMBS } from '../../ClientsPage.consts'

import '../../../../assets/css/pagination.css'
import { IsGranted } from '../../../auth/PrivateRoute'
import { TruncatedValue } from '../../../../common/TruncatedValue'
import { getErrorText } from '../../../../common/api/errors'

const breadCrumbs = [...CLIENTS_PAGE_BREADCRUMBS]

// Функция для маскировки номера карты
const maskCardNumber = (cardNumber: string) => {
  if (!cardNumber) return '-'
  return '**** **** **** ' + cardNumber.slice(-4)
}

// Функция для парсинга ошибок API
const parseApiError = (errors: any): string => {
  if (!errors) return 'Неизвестная ошибка'
  
  // Если ошибки в формате response.errors[key] - берем значение ошибки
  const errorKeys = Object.keys(errors)
  if (errorKeys.length > 0) {
    return errors[errorKeys[0]]
  }
  
  // Если другой формат - возвращаем целиком
  return JSON.stringify(errors)
}

export const ClientInfo = () => {
  const { currentUser } = useAuth()
  const { token } = useParams()
  const [client, setClient] = useState<IClientItem | undefined>(undefined)

  breadCrumbs.forEach((value, index) => {
    if (value.title === 'Инфо') {
      breadCrumbs[index].isActive = true
    }
    breadCrumbs[index].path = breadCrumbs[index].path.replace('__token__', token + '')
  })

  const getClientInfo = async () => {
    let response
    try {
      response = await getClientByToken(token + '', currentUser)
    } catch {
      // the common alert has shown the refusal (a malformed or unknown client token)
      return
    }

    if (response.data.success && response.data.externalUser) {
      const clientData = response.data.externalUser
      
      // Преобразуем paymentRequisites из объекта в массив, если это объект
      if (clientData.paymentRequisites && typeof clientData.paymentRequisites === 'object' && !Array.isArray(clientData.paymentRequisites)) {
        clientData.paymentRequisites = Object.values(clientData.paymentRequisites)
      }
      
      setClient(clientData)
    }
  }

  const handleDetachRequisite = async (requisiteId: number, requisiteName: string) => {
    const result = await Swal.fire({
      title: 'Подтверждение',
      text: 'Вы действительно хотите отвязать реквизит от клиента?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Да',
      cancelButtonText: 'Отмена',
      reverseButtons: false
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      const response = await detachPaymentRequisite(token + '', requisiteId)
      
      if (response.data.success && response.data.externalUser) {
        // Показываем сообщение об успехе на 1 секунду
        await Swal.fire({
          title: 'Успешно!',
          text: 'Реквизит отвязан от клиента',
          icon: 'success',
          timer: 1000,
          showConfirmButton: false
        })
        
        // Обновляем данные клиента из ответа
        const clientData = response.data.externalUser
        
        // Преобразуем paymentRequisites из объекта в массив, если это объект
        if (clientData.paymentRequisites && typeof clientData.paymentRequisites === 'object' && !Array.isArray(clientData.paymentRequisites)) {
          clientData.paymentRequisites = Object.values(clientData.paymentRequisites)
        }
        
        setClient(clientData)
      } else if (response.data.errors) {
        // Показываем ошибку
        const errorMessage = parseApiError(response.data.errors)
        Swal.fire({
          title: 'Ошибка!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        })
      }
    } catch (error: unknown) {
      const errorMessage = getErrorText(error, 'Произошла ошибка при отвязке реквизита', { withFieldName: false })
      
      Swal.fire({
        title: 'Ошибка!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      })
    }
  }

  useEffect(() => {
    getClientInfo()
  }, [false])

  return !client ? (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs}>Клиент инфо</PageTitle>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column text-center'>
            <span className='card-label fw-bold fs-3 mb-1'>Идёт загрузка данных клиента...</span>
          </h3>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs}>Клиент инфо</PageTitle>
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Клиент: {token}</span>
          </h3>
        </div>
      </div>

      <div className='d-flex flex-column flex-md-row gap-2'>
        <div className='card w-100'>
          <div className='card-body p-9'>
            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <div className='fs-6 d-flex justify-content-between mb-4'>
                <div className='fw-bold'>Флаг</div>
                <div className='d-flex'>{client.options?.color}</div>
              </div>
            )}

            {client.id ? (
              <div className='fs-6 d-flex justify-content-between mb-4'>
                <div className='fw-bold'>ID пользователя</div>
                <div className='d-flex'>{client.id}</div>
              </div>
            ) : (
              ''
            )}

            <div className='fs-6 d-flex justify-content-between my-4'>
              <div className='fw-bold flex-shrink-0'>Внешний ID</div>
              <div className='d-flex justify-content-end' style={{ minWidth: 0 }}>
                <TruncatedValue value={client.externalId} />
              </div>
            </div>

            <div className='separator separator-dashed'></div>

            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <div className='fs-6 d-flex justify-content-between my-4'>
                <div className='fw-bold'>Мерчант</div>
                <div>{client.merchant.title}</div>
              </div>
            )}

            <div className='separator separator-dashed'></div>

            <div className='fs-6 d-flex justify-content-between my-4'>
              <div className='fw-bold'>Создан</div>
              <div className='d-flex'>{formatInTimeZone(parseISO(client.createdAt), '+03:00', 'dd.MM.yyyy HH:mm')}</div>
            </div>

            <div className='fs-6 d-flex justify-content-between my-4'>
              <div className='fw-bold'>Последняя активность</div>
              <div className='d-flex'>
                {client.lastActivityDate
                  ? formatInTimeZone(parseISO(client?.lastActivityDate), '+03:00', 'dd.MM.yyyy HH:mm')
                  : ''}
              </div>
            </div>

            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MATCHING_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <>
                <div className='separator separator-dashed'></div>

                <div className='fs-6 d-flex justify-content-between my-4'>
                  <div className='fw-bold me-20'>Комментарий</div>
                  <div className='d-flex text-end text-break' style={{ whiteSpace: 'pre-wrap' }}>
                    {client.comment}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {!IsGranted([ROLE.ROLE_MATCHING_OPERATOR], currentUser) && (
        <div className='card w-100'>
          <div className='card-header pb-0'>
            <div className='card-title'>
              <h3 className='m-0'>Карты клиента</h3>
            </div>
          </div>
          <div className='card-body'>
            {client.requisites.map((requisite) => {
              return (
                <div key={requisite.token}>
                  <div className='d-flex flex-column gap-2 fs-6 pb-2'>
                    <div>
                      <span className='fw-bold'>Номер карты:</span> {requisite.identifier}
                    </div>
                    <div>
                      <span className='fw-bold'>Платежный метод:</span>{' '}
                      {requisite?.paymentMethodType ?? '-'}
                    </div>
                    <div>
                      <span className='fw-bold'>Валюту:</span> {requisite.currency ?? '-'}
                    </div>
                    <div>
                      <span className='fw-bold'>Банк-эквайер:</span>{' '}
                      {requisite.acquiringBank ?? '-'}
                    </div>
                    <div>
                      <span className='fw-bold'>Держатель:</span> {requisite.cardholder ?? '-'}
                    </div>
                    <div>
                      <span className='fw-bold'>Комментарий:</span> {requisite.comment ?? '-'}
                    </div>
                  </div>
                  <div className='separator separator-dashed mb-4'></div>
                </div>
              )
            })}
          </div>
        </div>
        )}
      </div>

            {/* Новый блок "Реквизиты для пополнени��" */}
      {!IsGranted([ROLE.ROLE_MATCHING_OPERATOR], currentUser) && (
      <div className='card w-100 mt-4'>
        <div className='card-header pb-0'>
          <div className='card-title'>
            <h3 className='m-0'>Реквизиты для пополнения</h3>
          </div>
        </div>
        <div className='card-body'>
          {client.paymentRequisites && client.paymentRequisites.length > 0 ? (
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th className='min-w-50px'>ID</th>
                    {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                      <th className='min-w-150px'>Название</th>
                    )}
                    <th className='min-w-100px'>Тип</th>
                    <th className='min-w-150px'>Номер карты</th>
                    {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_PLATFORM, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                      <th className='min-w-120px'>Телефон</th>
                    )}
                    {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                      <th className='min-w-100px'>Баланс</th>
                    )}
                    <th className='min-w-80px'>Статус</th>
                    {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_PLATFORM, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                      <th className='min-w-120px'>Банк</th>
                    )}
                    {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                      <th className='min-w-100px text-end'>Действия</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {client.paymentRequisites.map((requisite) => (
                    <tr key={requisite.id}>
                      <td>
                        <div className='text-dark fw-bold fs-6'>
                          {requisite.id}
                        </div>
                      </td>
                      {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                        <td>
                          <div className='text-dark fw-bold fs-6'>
                            {requisite.name}
                          </div>
                        </td>
                      )}
                      <td>
                        <div className='text-dark fw-bold fs-6'>
                          {requisite.requisiteType}
                        </div>
                      </td>
                      <td>
                        <div className='text-dark fw-bold fs-6'>
                          {maskCardNumber(requisite.cardNumber)}
                        </div>
                      </td>
                      {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_PLATFORM, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                        <td>
                          <div className='text-muted fs-7'>
                            {requisite.phone ?? '-'}
                          </div>
                        </td>
                      )}
                      {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                        <td>
                          <div className='text-dark fs-6'>
                            {requisite.balance} {requisite.currency}
                          </div>
                        </td>
                      )}
                      <td>
                        <span className={`badge ${requisite.enabled ? 'badge-light-success' : 'badge-light-danger'}`}>
                          {requisite.enabled ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_CASHIER, ROLE.ROLE_PLATFORM, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                        <td>
                          <div className='text-muted fs-7'>
                            {requisite.acquiringBank || '-'}
                          </div>
                        </td>
                      )}
                      {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                        <td className='text-end'>
                          <button
                            className='btn btn-sm btn-danger'
                            onClick={() => handleDetachRequisite(requisite.id, requisite.name)}
                          >
                            Отвязать
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-muted text-center py-4'>
              <div className='fs-6'>Нет привязанных реквизитов для пополнения</div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  )
}
